import React, { useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import Highcharts3D from "highcharts/highcharts-3d";
import GridLight from "highcharts/themes/grid-light";

// Initialize 3D module and theme
Highcharts3D(Highcharts);
GridLight(Highcharts);

const PlatformPostsBarChart = ({ data }) => {
  const [chartOptions, setChartOptions] = useState({});

  useEffect(() => {
    if (!Array.isArray(data)) {
      console.error("Data should be an array");
      return;
    }

    // Transform the server data into platform totals
    const platformData = transformDataToPlatformTotals(data);

    const options = {
      credits: {
        enabled: false,
      },
      chart: {
        type: "column",
        backgroundColor: "#ffffff",
        options3d: {
          enabled: true,
          alpha: 10,
          beta: 13,
          depth: 47,
          viewDistance: 80,
        },
      },
      title: {
        text: "Platform Posts Analysis - Crawled vs Unlawful",
        style: {
          fontSize: "18px",
          fontWeight: "bold",
        },
      },
      subtitle: {
        text: "Percentage breakdown of crawled posts vs unlawful posts by platform",
        style: {
          fontSize: "14px",
          color: "#666",
        },
      },
      xAxis: {
        categories: platformData.platforms,
        title: {
          text: "Platforms",
          style: {
            fontSize: "14px",
            fontWeight: "bold",
          },
        },
        labels: {
          style: {
            fontSize: "12px",
            fontWeight: "bold",
          },
        },
      },
      yAxis: {
        min: 0,
        max: 100,
        title: {
          text: "Percentage (%)",
          style: {
            fontSize: "14px",
            fontWeight: "bold",
          },
        },
        labels: {
          formatter: function () {
            return this.value + "%";
          },
        },
        stackLabels: {
          enabled: true,
          style: {
            fontWeight: "bold",
            color: "white",
          },
          formatter: function () {
            return "Total: " + platformData.totalPosts[this.x];
          },
        },
      },
      legend: {
        align: "center",
        verticalAlign: "bottom",
        backgroundColor: "white",
        borderColor: "#CCC",
        borderWidth: 1,
        shadow: false,
      },
      tooltip: {
        headerFormat: "<b>{point.x}</b><br/>",
        pointFormat:
          "{series.name}: <b>{point.y:.1f}%</b><br/>" +
          "Actual Count: <b>{point.actualCount}</b><br/>" +
          "Total Posts: <b>{point.totalPosts}</b>",
      },
      plotOptions: {
        column: {
          stacking: "normal",
          depth: 25,
          dataLabels: {
            enabled: true,
            format: "{point.y:.1f}%",
            style: {
              fontSize: "15px",
              fontWeight: "bold",
              textOutline: "1px contrast",
            },
          },
        },
      },
      colors: ["#45FFCA", "#FF7777", "#D67BFF", "#5B8FB9", "#FFB6D9"],
      series: [
        {
          name: "Lawful Posts",
          data: platformData.lawfulPercentages.map((percentage, index) => ({
            y: percentage,
            actualCount: platformData.lawfulCounts[index],
            totalPosts: platformData.totalPosts[index],
          })),
          color: "#45FFCA",
        },
        {
          name: "Unlawful Posts",
          data: platformData.unlawfulPercentages.map((percentage, index) => ({
            y: percentage,
            actualCount: platformData.unlawfulCounts[index],
            totalPosts: platformData.totalPosts[index],
          })),
          color: "#FF7777",
        },
      ],
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 600,
            },
            chartOptions: {
              chart: {
                options3d: {
                  enabled: true,
                  alpha: 10,
                  beta: 13,
                  depth: 47,
                  viewDistance: 80,
                },
              },
              title: {
                style: {
                  fontSize: "16px",
                },
              },
              subtitle: {
                style: {
                  fontSize: "12px",
                },
              },
              xAxis: {
                labels: {
                  style: {
                    fontSize: "10px",
                  },
                },
              },
              yAxis: {
                labels: {
                  style: {
                    fontSize: "10px",
                  },
                },
              },
              plotOptions: {
                column: {
                  dataLabels: {
                    style: {
                      fontSize: "8px",
                    },
                  },
                },
              },
            },
          },
        ],
      },
    };

    setChartOptions(options);
  }, [data]);

  const transformDataToPlatformTotals = (data) => {
    const platformMap = {};

    // Aggregate data by platform
    data.forEach((item) => {
      const { platform, postCount, unlawfulCount } = item;

      if (!platformMap[platform]) {
        platformMap[platform] = {
          totalPosts: 0,
          totalUnlawful: 0,
        };
      }

      platformMap[platform].totalPosts += postCount || 0;
      platformMap[platform].totalUnlawful += unlawfulCount || 0;
    });

    // Calculate percentages and prepare data arrays
    const platforms = Object.keys(platformMap);
    const totalPosts = [];
    const unlawfulCounts = [];
    const lawfulCounts = [];
    const unlawfulPercentages = [];
    const lawfulPercentages = [];

    platforms.forEach((platform) => {
      const total = platformMap[platform].totalPosts;
      const unlawful = platformMap[platform].totalUnlawful;
      const lawful = total - unlawful;

      totalPosts.push(total);
      unlawfulCounts.push(unlawful);
      lawfulCounts.push(lawful);

      if (total > 0) {
        unlawfulPercentages.push((unlawful / total) * 100);
        lawfulPercentages.push((lawful / total) * 100);
      } else {
        unlawfulPercentages.push(0);
        lawfulPercentages.push(0);
      }
    });

    return {
      platforms,
      totalPosts,
      unlawfulCounts,
      lawfulCounts,
      unlawfulPercentages,
      lawfulPercentages,
    };
  };

  // Handle window resize for chart responsiveness
  useEffect(() => {
    const handleResize = () => {
      Highcharts.charts.forEach((chart) => {
        if (chart) {
          chart.reflow();
        }
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return <HighchartsReact highcharts={Highcharts} options={chartOptions} />;
};

export default PlatformPostsBarChart;
